import React, { useState, useMemo } from 'react';
import { Info, TrendingUp, DollarSign, Shield, Heart, Users, Target, AlertTriangle, X } from 'lucide-react';
import { normalizeCompanyScores } from '../../../utils/jobScoreCalculator';

const CompanyInsightsPanel = ({ company, onClose }) => {
    const [expandedSection, setExpandedSection] = useState(null);

    console.log("🎨 [CompanyInsightsPanel] Received company:", company);

    // Normalize scores from any format (old DB format, new backend format, etc.)
    // Hooks must be called unconditionally before any early returns
    const rawScores = company?.scores || {};
    const normalizedScores = useMemo(() => normalizeCompanyScores(rawScores), [rawScores]);
    
    // Map normalized scores back to the format expected by scoreCategories
    const scores = useMemo(() => {
        const mapped = {};
        
        // Map normalized frontend keys to backend format keys expected by scoreCategories
        const mapping = {
            'compensation': 'compensation',
            'stability': 'stability',
            'growth': 'careerGrowth',  // Frontend 'growth' → Backend 'careerGrowth'
            'WLB': 'workLifeBalance',  // Frontend 'WLB' → Backend 'workLifeBalance'
            'toxicity': 'toxicity'
        };
        
        // For each category, create the expected structure with weightedScore
        for (const [normalizedKey, backendKey] of Object.entries(mapping)) {
            if (typeof normalizedScores[normalizedKey] === 'number') {
                // Check if raw scores have bulletPoints and keyInsights
                const rawCategory = rawScores[backendKey] || rawScores[normalizedKey];
                mapped[backendKey] = {
                    weightedScore: normalizedScores[normalizedKey],
                    bulletPoints: rawCategory?.bulletPoints || [],
                    keyInsights: rawCategory?.keyInsights || []
                };
            }
        }
        
        return mapped;
    }, [normalizedScores, rawScores]);
    
    const scoresStatus = company?.scoresStatus || 'pending';

    // Early return must come AFTER all hooks
    if (!company) {
        console.log("🎨 [CompanyInsightsPanel] No company data, showing empty state");
        return (
            <div className="hidden lg:flex lg:w-96 bg-white border-l border-gray-200 overflow-y-auto flex-shrink-0">
                <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                    <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                        <Target className="w-10 h-10 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Select a Job</h3>
                    <p className="text-sm text-gray-500">
                        Click on any job card to view detailed company insights and scores
                    </p>
                </div>
            </div>
        );
    }

    console.log("🎨 [CompanyInsightsPanel] Rendering panel with company:", company.name);

    // Score categories configuration (updated order and label for Toxicity)
    const scoreCategories = [
        {
            key: 'compensation',
            label: 'Compensation',
            icon: DollarSign,
            color: 'green',
            description: 'Salary and benefits package',
        },
        {
            key: 'workLifeBalance',
            label: 'Work-Life Balance',
            icon: Heart,
            color: 'purple',
            description: 'Flexibility and employee wellbeing',
        },
        {
            key: 'careerGrowth',
            label: 'Career Growth',
            icon: TrendingUp,
            color: 'orange',
            description: 'Learning and advancement opportunities',
        },
        {
            key: 'toxicity',
            label: 'Toxicity', // was Culture Health - changed as requested
            icon: AlertTriangle,
            color: 'red',
            description: 'Work environment and culture',
        },
        {
            key: 'stability',
            label: 'Stability',
            icon: Shield,
            color: 'blue',
            description: 'Financial health and job security',
        },
    ];

    // True when not one category came back with a usable score, i.e. the company
    // was never successfully researched rather than simply rating poorly.
    const noCategoryHasData = scoreCategories.every(
        (c) => scores[c.key]?.weightedScore == null
    );

    const getScoreColor = (score, inverted = false) => {
        if (!score) return 'gray';
        const actualScore = inverted ? 100 - score : score;
        if (actualScore >= 80) return 'green';
        if (actualScore >= 60) return 'yellow';
        if (actualScore >= 40) return 'orange';
        return 'red';
    };
    const getColorClasses = (color, type = 'bg') => {
        const colors = {
            blue: type === 'bg' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'text-blue-600',
            purple: type === 'bg' ? 'bg-purple-100 text-purple-700 border-purple-200' : 'text-purple-600',
            green: type === 'bg' ? 'bg-green-100 text-green-700 border-green-200' : 'text-green-600',
            orange: type === 'bg' ? 'bg-orange-100 text-orange-700 border-orange-200' : 'text-orange-600',
            red: type === 'bg' ? 'bg-red-100 text-red-700 border-red-200' : 'text-red-600',
            indigo: type === 'bg' ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'text-indigo-600',
            yellow: type === 'bg' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' : 'text-yellow-600',
            gray: type === 'bg' ? 'bg-gray-100 text-gray-700 border-gray-200' : 'text-gray-600',
        };
        return colors[color] || colors.gray;
    };

    // Updated ScoreCard: always expanded, only score and bullet points shown
    const ScoreCard = ({ category }) => {
        const categoryData = scores[category.key];
        const score = categoryData?.weightedScore;
        const Icon = category.icon;
        const scoreColor = getScoreColor(score, category.inverted);

        // Function to get score text. For Toxicity, use the requested labels and invert score
        const getScoreText = (score) => {
            // "N/A" reads like a rating that failed to load; this says plainly
            // that there was nothing to rate.
            if (score === undefined || score === null) return 'No data';
            if (category.key === 'toxicity') {
                if (score >= 80) return 'No Toxicity Signals Identified';
                if (score >= 60) return 'Low Toxicity';
                if (score >= 40) return 'Average';
                if (score >= 25) return 'Possibly Toxic';
                return 'High Toxicity Signals Identified';
            }

            if (score >= 85) return 'Excellent';
            if (score >= 70) return 'Very Good';
            if (score >= 55) return 'Good';
            if (score >= 40) return 'Moderate';
            if (score >= 25) return 'Fair';
            return 'Limited';
        };

        return (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm mb-3">
                <div className="flex items-center gap-3 p-4">
                            <div className={`w-10 h-10 rounded-lg ${getColorClasses(category.color, 'bg')} flex items-center justify-center`}>
                                <Icon className="w-5 h-5" />
                            </div>
                    <div className="flex items-center gap-3">
                                <h4 className="font-bold text-gray-800 text-base">{category.label}</h4>
                                <div className={`px-3 py-1 rounded-lg font-bold text-sm ${getColorClasses(scoreColor, 'bg')}`}>
                            {getScoreText(score)}
                        </div>
                    </div>
                </div>
                {/* With no score, the research step's bullets are three restatements
                    of "not available" — they fill the card without telling the user
                    anything. One muted line carries the same meaning. */}
                {score == null ? (
                    <p className="px-6 pb-4 text-sm text-gray-400 text-left">
                        No public information found for this company.
                    </p>
                ) : (
                    categoryData?.bulletPoints?.length > 0 && (
                        <ul className="px-6 pb-4 space-y-2 text-left">
                            {categoryData.bulletPoints.map((point, idx) => (
                                <li key={idx} className="text-sm text-gray-700 list-disc ml-5 text-left">{point}</li>
                            ))}
                        </ul>
                    )
                )}
            </div>
        );
    };

    return (
        <div className="hidden lg:flex lg:w-100 bg-white border-l border-gray-200 overflow-y-auto flex-shrink-0 flex-col">
            {/* Company Profile Header */}
            <div className="bg-white border-b border-gray-200 p-6">
                <div className="flex items-start gap-4 mb-4">
                    <img
                        src={company.companyLogo || 'https://cdn-icons-png.flaticon.com/512/2721/2721294.png'}
                        alt={company.name}
                        className="w-16 h-16 rounded-lg object-contain bg-gray-50 border border-gray-200 flex-shrink-0"
                        onError={(e) => { 
                            e.target.src = 'https://cdn-icons-png.flaticon.com/512/2721/2721294.png'; 
                        }}
                    />
                    <div className="flex-1 min-w-0">
                        <h2 className="text-xl font-semibold text-gray-900 mb-1 text-left">{company.name}</h2>
                        {company.hqLocation && (
                            <p className="text-sm text-gray-600 flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {company.hqLocation}
                            </p>
                        )}
                        {company.email && company.email !== 'N/A' && (
                            <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                {company.email}
                            </p>
                        )}
                    </div>
                </div>

                {/* Company Description */}
                {company.description && company.description !== 'No description available' && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-sm text-gray-600 leading-relaxed line-clamp-4 text-left">
                            {company.description}
                        </p>
                    </div>
                )}

                {/* Overall Fit Score */}
                {company.fitScore && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Overall Fit Score</span>
                            <span className={`text-2xl font-bold ${getColorClasses(getScoreColor(company.fitScore), 'text')}`}>
                                {Math.round(company.fitScore)}
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="h-2 rounded-full bg-blue-600 transition-all duration-500"
                                style={{ width: `${company.fitScore}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Company Insights Section */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-800">Company Insights</h3>
            </div>

            {/* Content */}
            <div className="p-4 space-y-3 bg-gray-50">
                {scoresStatus === 'pending' && (
                    <div className="text-center py-8">
                        <div className="inline-block w-8 h-8 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mb-3"></div>
                        <p className="text-sm text-gray-700 font-medium">Calculating company scores...</p>
                        <p className="text-xs text-gray-500 mt-1">This may take a few moments</p>
                    </div>
                )}

                {scoresStatus === 'failed' && (
                    <div className="text-center py-8">
                        <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-700 font-medium">Unable to calculate scores</p>
                        <p className="text-xs text-gray-500 mt-1">Limited data available for this company</p>
                    </div>
                )}

                {scoresStatus === 'success' && (
                    <>
                        {/* When nothing could be researched, six identical "No data" cards
                            is a wall of nothing. Explain it once, say why, and tell the
                            user what they can still do. Individual cards are only worth
                            showing when at least one of them carries a real rating. */}
                        {noCategoryHasData ? (
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
                                <Info className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                                <p className="text-sm font-semibold text-gray-700">
                                    No company insights available
                                </p>
                                <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                                    We could not find enough public information about{' '}
                                    {company.name || 'this company'} to rate compensation,
                                    work-life balance, stability, career growth or culture.
                                    This is common for smaller and privately held employers.
                                </p>
                                <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                                    The referral contacts on this job are still the fastest way
                                    to learn what it is actually like to work here.
                                </p>
                            </div>
                        ) : (
                            scoreCategories.map((category) => (
                                <ScoreCard key={category.key} category={category} />
                            ))
                        )}

                        {/*
                        {company.redFlags && company.redFlags.length > 0 && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                <h4 className="font-semibold text-sm text-red-800 mb-2 flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" />
                                    Red Flags
                                </h4>
                                <ul className="space-y-1">
                                    {company.redFlags.map((flag, idx) => (
                                        <li key={idx} className="text-xs text-red-700 flex items-start gap-2">
                                            <span className="text-red-500 mt-1">⚠</span>
                                            <span>{flag}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        */}

                        {/*
                        {company.limitations && company.limitations.length > 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                                <h4 className="font-semibold text-sm text-yellow-800 mb-2 flex items-center gap-2">
                                    <Info className="w-4 h-4" />
                                    Data Limitations
                                </h4>
                                <ul className="space-y-1">
                                    {company.limitations.map((limitation, idx) => (
                                        <li key={idx} className="text-xs text-yellow-700 flex items-start gap-2">
                                            <span className="text-yellow-500 mt-1">ℹ</span>
                                            <span>{limitation}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        */}

                        {/* Subtle note removed; methodology is referenced in header for a cleaner, professional look */}
                    </>
                )}
            </div>
        </div>
    );
};

export default CompanyInsightsPanel;


